import { useLanguage } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Info } from "lucide-react";
import { WaterEntry } from "@/types/water";

interface WaterSourcesTabProps {
  formEntry: WaterEntry;
  onFormEntryChange: (entry: WaterEntry) => void;
  waterSources: string[];
  errors: Record<string, string>;
  touched: Record<string, boolean>;
  onFieldChange: (field: string, value: any) => void;
  onFieldBlur: (field: string) => void;
}

export const WaterSourcesTab = ({
  formEntry,
  onFormEntryChange,
  waterSources,
  errors,
  touched,
  onFieldChange,
  onFieldBlur,
}: WaterSourcesTabProps) => {
  const { t } = useLanguage();

  const handleWaterSurfaceAreaChange = (value: string) => {
    onFieldChange("waterSurfaceArea", value);
    onFormEntryChange({
      ...formEntry,
      waterSurfaceArea: value === "" ? "" : Number(value),
    });
  };

  const handleSourceToggle = (sourceIndex: number, checked: boolean) => {
    const sourceName = waterSources[sourceIndex];
    const sourceId = sourceIndex.toString();
    let newOrigins = [...formEntry.waterOrigins];

    if (checked) {
      newOrigins.push({
        sourceId: sourceId,
        sourceName: sourceName,
        quantity: "" as number | "",
        unit: "m³",
      });
    } else {
      newOrigins = newOrigins.filter((origin) => origin.sourceId !== sourceId);
    }

    onFieldChange("waterOrigins", newOrigins);
    onFormEntryChange({
      ...formEntry,
      waterOrigins: newOrigins,
    });
  };

  const handleQuantityChange = (sourceId: string, newQuantity: string) => {
    const newOrigins = formEntry.waterOrigins.map((origin) =>
      origin.sourceId === sourceId
        ? { ...origin, quantity: newQuantity === "" ? "" : parseFloat(newQuantity) as number | "" }
        : origin
    );
    onFieldChange(`origin_quantity_${sourceId}`, newQuantity);
    onFormEntryChange({
      ...formEntry,
      waterOrigins: newOrigins,
    });
  };

  return (
    <div className="space-y-6">
      {/* Water Surface Area Section */}
      <div className="space-y-3">
        <Label htmlFor="waterSurfaceArea" className="text-base text-gray-700">
          {t("water.waterSurfaceArea")} *
        </Label>
        <div className="relative">
          <Input
            id="waterSurfaceArea"
            type="number"
            min="0"
            step="0.01"
            value={formEntry.waterSurfaceArea ?? ""}
            onChange={(e) => handleWaterSurfaceAreaChange(e.target.value)}
            onBlur={() => onFieldBlur("waterSurfaceArea")}
            className={`pr-12 h-12 text-base ${
              touched.waterSurfaceArea && errors.waterSurfaceArea
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-200 focus:ring-teal-500 focus:border-teal-500"
            }`}
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <span className="text-gray-500 text-base">m²</span>
          </div>
        </div>
        {errors.waterSurfaceArea && (
          <div className="text-red-500 text-sm flex items-center gap-1 mt-1">
            <Info className="h-3 w-3" />
            {errors.waterSurfaceArea}
          </div>
        )}
      </div>

      {/* Water Origins Section */}
      <div className="space-y-3 pt-2 mt-6">
        <Label
          className={`text-base ${
            errors.waterOrigins ? "text-red-500" : "text-gray-700"
          }`}
        >
          {t("water.waterOrigins")} *
        </Label>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {waterSources.length === 0 ? (
            <div className="text-gray-500 text-base italic text-center py-6">
              {t("water.noWaterSourcesAvailable")}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 p-4 bg-gray-50 border-b border-gray-200">
                {waterSources.map((sourceName, index) => {
                  const sourceId = index.toString();
                  const isSelected = formEntry.waterOrigins.some(
                    (origin) => origin.sourceId === sourceId
                  );
                  return (
                    <button
                      key={sourceId}
                      type="button"
                      onClick={() => handleSourceToggle(index, !isSelected)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all duration-200 ${
                        isSelected
                          ? "bg-teal-600 text-white hover:bg-teal-700"
                          : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                      }`}
                    >
                      {sourceName}
                    </button>
                  );
                })}
              </div>
              {formEntry.waterOrigins.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {formEntry.waterOrigins.map((origin) => {
                    const sourceIndex = parseInt(origin.sourceId);
                    const sourceName =
                      waterSources[sourceIndex] || origin.sourceName;
                    const quantityError =
                      errors[`origin_quantity_${origin.sourceId}`];
                    return (
                      <div key={origin.sourceId} className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center min-w-0 flex-1 mr-3">
                            <div className="h-2 w-2 bg-teal-600 rounded-full mr-2 flex-shrink-0" />
                            <span className="text-sm font-medium text-gray-800 truncate">
                              {sourceName}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 w-full max-w-[280px] flex-shrink-0">
                            <div className="relative w-full">
                              <Input
                                id={`quantity-${origin.sourceId}`}
                                type="number"
                                min="0"
                                step="0.01"
                                value={origin.quantity === "" ? "" : origin.quantity}
                                onChange={(e) =>
                                  handleQuantityChange(
                                    origin.sourceId,
                                    e.target.value
                                  )
                                }
                                onBlur={() =>
                                  onFieldBlur(
                                    `origin_quantity_${origin.sourceId}`
                                  )
                                }
                                placeholder="0.00"
                                autoComplete="off"
                                className={`pr-12 h-11 w-full rounded-md text-base ${
                                  touched[
                                    `origin_quantity_${origin.sourceId}`
                                  ] && quantityError
                                    ? "border-red-500 focus:ring-red-500"
                                    : "border-gray-300 focus:ring-teal-500 focus:border-teal-500"
                                }`}
                              />
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <span className="text-gray-500 text-sm">
                                  m³
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                handleSourceToggle(sourceIndex, false)
                              }
                              className="text-gray-400 hover:text-red-500 transition-colors p-1.5 rounded-full hover:bg-red-50 flex-shrink-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        {quantityError && (
                          <div className="text-red-500 text-sm flex items-center gap-1 mt-1.5 ml-4">
                            <Info className="h-3 w-3" />
                            {quantityError}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-6 px-4 text-center text-gray-500 text-sm bg-gray-25">
                  <div className="flex items-center justify-center gap-1.5">
                    <Info className="h-4 w-4 text-gray-400" />
                    <span>{t("water.selectWaterSourcesAbove")}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {errors.waterOrigins && (
          <div className="text-red-500 text-sm flex items-center gap-1 mt-1">
            <Info className="h-3 w-3" />
            {errors.waterOrigins}
          </div>
        )}
      </div>

      {/* Discharge Decision Section */}
      <div className="space-y-3 pt-6 border-t border-gray-200">
        <Label className="text-base text-gray-700">
          {t("water.dischargeQuestion")} *
        </Label>
        <div className="flex gap-1 max-w-[200px]">
          <button
            type="button"
            onClick={() => {
              const hasDischarge = false;
              onFieldChange("hasDischarge", hasDischarge);
              onFormEntryChange({
                ...formEntry,
                discharge: {
                  ...formEntry.discharge,
                  hasDischarge,
                  dischargeEntries: [],
                  pollutants: [],
                },
              });
            }}
            className={`flex-1 h-10 text-sm rounded-lg border transition-all duration-200 ${
              formEntry.discharge.hasDischarge === false
                ? "bg-red-600 text-white border-red-600 shadow-sm"
                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
            }`}
          >
            {t("common.no")}
          </button>
          <button
            type="button"
            onClick={() => {
              const hasDischarge = true;
              onFieldChange("hasDischarge", hasDischarge);
              onFormEntryChange({
                ...formEntry,
                discharge: {
                  ...formEntry.discharge,
                  hasDischarge,
                },
              });
            }}
            className={`flex-1 h-10 text-sm rounded-lg border transition-all duration-200 ${
              formEntry.discharge.hasDischarge === true
                ? "bg-teal-600 text-white border-teal-600 shadow-sm"
                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
            }`}
          >
            {t("common.yes")}
          </button>
        </div>
        {formEntry.discharge.hasDischarge === null && errors.hasDischarge && (
          <div className="text-red-500 text-sm flex items-center gap-1 mt-1">
            <Info className="h-3 w-3" />
            {errors.hasDischarge}
          </div>
        )}
        {formEntry.discharge.hasDischarge === true && (
          <div className="text-blue-600 text-sm flex items-center gap-1 mt-2 p-2 bg-blue-50 rounded-md border border-blue-200">
            <Info className="h-3 w-3" />
            {t("water.fillTabMessage", { tabName: t("water.discharge") })}
          </div>
        )}
      </div>

      {/* Sludge Decision Section */}
      <div className="space-y-3 pt-6 pb-8 border-t border-gray-200">
        <Label className="text-base text-gray-700">
          {t("water.sludgeQuestion")} *
        </Label>
        <div className="flex gap-1 max-w-[200px]">
          <button
            type="button"
            onClick={() => {
              const hasSludge = false;
              onFieldChange("hasSludge", hasSludge);
              onFormEntryChange({
                ...formEntry,
                sludge: {
                  ...formEntry.sludge,
                  hasSludge,
                  sludgeQuantity: "",
                  sludgeUnit: "t",
                  moistureContent: "",
                  isDepositedInBasin: null,
                  pollutants: [],
                },
              });
            }}
            className={`flex-1 h-10 text-sm rounded-lg border transition-all duration-200 ${
              formEntry.sludge.hasSludge === false
                ? "bg-red-600 text-white border-red-600 shadow-sm"
                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
            }`}
          >
            {t("common.no")}
          </button>
          <button
            type="button"
            onClick={() => {
              const hasSludge = true;
              onFieldChange("hasSludge", hasSludge);
              onFormEntryChange({
                ...formEntry,
                sludge: {
                  ...formEntry.sludge,
                  hasSludge,
                },
              });
            }}
            className={`flex-1 h-10 text-sm rounded-lg border transition-all duration-200 ${
              formEntry.sludge.hasSludge === true
                ? "bg-teal-600 text-white border-teal-600 shadow-sm"
                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
            }`}
          >
            {t("common.yes")}
          </button>
        </div>
        {formEntry.sludge.hasSludge === null && errors.hasSludge && (
          <div className="text-red-500 text-sm flex items-center gap-1 mt-1">
            <Info className="h-3 w-3" />
            {errors.hasSludge}
          </div>
        )}
        {formEntry.sludge.hasSludge === true && (
          <div className="text-blue-600 text-sm flex items-center gap-1 mt-2 p-2 bg-blue-50 rounded-md border border-blue-200">
            <Info className="h-3 w-3" />
            {t("water.fillTabMessage", { tabName: t("water.sludge") })}
          </div>
        )}
      </div>
    </div>
  );
};
